import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useQuery } from "@tanstack/react-query";
import {
  CircleAlert,
  ExternalLink,
  FlaskConical,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { useRef } from "react";
import {
  fetchProteinDrugs,
  fetchProteinMeta,
  fetchProteinStructures,
  proteinCommands,
} from "../api/playground";

type SqlRow = Record<string, string | null>;

/** The command that produced a section, shown so users can replay it. */
const CommandLine = ({ command }: { command: string }) => (
  <code className="block truncate font-mono text-3xs text-zinc-400" title={command}>
    <span className="select-none">$ </span>paperclip {command}
  </code>
);

const SectionShell = ({
  title,
  count,
  command,
  loading,
  error,
  children,
}: {
  title: string;
  count?: number;
  command: string;
  loading: boolean;
  error: unknown;
  children: React.ReactNode;
}) => (
  <section className="border-b border-zinc-100 px-5 py-4">
    <div className="mb-1 flex items-baseline gap-2">
      <h3 className="text-2xs font-medium uppercase tracking-wide text-zinc-400">
        {title}
      </h3>
      {typeof count === "number" && (
        <span className="text-2xs tabular-nums text-zinc-400">{count}</span>
      )}
    </div>
    <CommandLine command={command} />
    <div className="mt-2.5">
      {loading ? (
        <div className="flex items-center gap-2 py-3 text-zinc-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          <span className="text-xs">Querying…</span>
        </div>
      ) : error ? (
        <p className="py-2 text-xs text-red-700" role="alert">
          {error instanceof Error ? error.message : "Query failed"}
        </p>
      ) : (
        children
      )}
    </div>
  </section>
);

const OverviewItem = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-3xs uppercase tracking-wide text-zinc-400">{label}</dt>
    <dd className="mt-0.5 text-sm text-zinc-800">{value}</dd>
  </div>
);

const phaseBadge = (row: SqlRow) => {
  const phase = parseFloat(row.max_phase ?? "");
  if (phase >= 4) {
    return (
      <Badge variant="green" className="text-3xs">
        Approved{row.first_approval ? ` ${row.first_approval}` : ""}
      </Badge>
    );
  }
  if (phase > 0) {
    return (
      <Badge className="bg-amber-50 text-3xs text-amber-700">
        Phase {Number.isInteger(phase) ? phase : phase.toFixed(1)}
      </Badge>
    );
  }
  return <Badge className="text-3xs">Preclinical</Badge>;
};

/**
 * Slide-over profile for a UniProt entry, assembled live from the same
 * commands a CLI user would run: `cat` for the entry metadata, then two
 * proteins-SQL queries joining PDB structures and ChEMBL drug mechanisms on
 * the accession. Every section shows its command.
 */
export const ProteinDetailPanel = ({
  accession,
  onClose,
  onSearchPapers,
}: {
  accession: string | null;
  onClose: () => void;
  onSearchPapers: (query: string) => void;
}) => {
  const isOpen = accession !== null;
  const panelRef = useRef<HTMLElement>(null);

  const metaQuery = useQuery({
    queryKey: ["playground", "protein-meta", accession],
    queryFn: () => fetchProteinMeta(accession!),
    enabled: isOpen,
    staleTime: Infinity,
  });
  const structuresQuery = useQuery({
    queryKey: ["playground", "protein-structures", accession],
    queryFn: () => fetchProteinStructures(accession!),
    enabled: isOpen,
    staleTime: Infinity,
  });
  const drugsQuery = useQuery({
    queryKey: ["playground", "protein-drugs", accession],
    queryFn: () => fetchProteinDrugs(accession!),
    enabled: isOpen,
    staleTime: Infinity,
  });

  useDrawerA11y({ isOpen, onClose, containerRef: panelRef, enabled: isOpen });

  if (!isOpen) return null;

  const meta = metaQuery.data;
  const gene = meta?.gene_name ?? accession;

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        aria-hidden
        className="absolute inset-0 bg-zinc-900/40"
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Protein ${accession}`}
        tabIndex={-1}
        className="absolute bottom-0 right-0 top-0 flex w-[min(44rem,94vw)] flex-col bg-white shadow-[-8px_0_32px_rgba(0,0,0,0.15)]"
      >
        <header className="shrink-0 border-b border-zinc-200 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Badge variant="blue" className="text-3xs uppercase tracking-wide">
              UniProt
            </Badge>
            <span className="font-mono text-2xs text-zinc-400">{accession}</span>
            {meta?.uniprot_id && (
              <span className="font-mono text-2xs text-zinc-400">
                {meta.uniprot_id}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <a
                href={`https://www.uniprot.org/uniprotkb/${accession}/entry`}
                target="_blank"
                rel="noreferrer"
                title="Open on uniprot.org"
                className="inline-flex h-7 w-7 items-center justify-center rounded-[3px] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                <span className="sr-only">Open on uniprot.org</span>
              </a>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close protein panel"
                onClick={onClose}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
          {metaQuery.isPending ? (
            <div className="mt-2 h-4 w-56 animate-pulse rounded bg-zinc-100" />
          ) : metaQuery.error ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-700" role="alert">
              <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {metaQuery.error instanceof Error
                ? metaQuery.error.message
                : "Failed to load protein"}
            </p>
          ) : (
            <>
              <h2 className="mt-1.5 text-base leading-7 text-zinc-900">
                {meta?.gene_name ? `${meta.gene_name} — ` : ""}
                {meta?.protein_name ?? accession}
              </h2>
              <p className="mt-0.5 text-xs text-zinc-500">
                {[
                  meta?.organism &&
                  `${meta.organism}${meta?.organism_common ? ` (${meta.organism_common})` : ""}`,
                  meta?.entry_type,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <SectionShell
            title="Entry"
            command={proteinCommands.meta(accession)}
            loading={metaQuery.isPending}
            error={metaQuery.error}
          >
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
              {meta?.sequence_length != null && (
                <OverviewItem label="Length" value={`${meta.sequence_length} aa`} />
              )}
              {meta?.mol_weight != null && (
                <OverviewItem
                  label="Mass"
                  value={`${(meta.mol_weight / 1000).toFixed(1)} kDa`}
                />
              )}
              {meta?.annotation_score != null && (
                <OverviewItem
                  label="Annotation"
                  value={`${meta.annotation_score}/5`}
                />
              )}
              {meta?.taxon_id != null && (
                <OverviewItem label="Taxon ID" value={String(meta.taxon_id)} />
              )}
            </dl>
          </SectionShell>

          <SectionShell
            title="3D structures — PDB"
            count={structuresQuery.data?.length}
            command={proteinCommands.structures(accession)}
            loading={structuresQuery.isPending}
            error={structuresQuery.error}
          >
            {(structuresQuery.data ?? []).length === 0 ? (
              <p className="py-1 text-xs text-zinc-500">
                No experimental structures deposited for this entry.
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {(structuresQuery.data ?? []).map((row) => (
                  <li
                    key={row.entry_id}
                    className="flex items-center gap-2 rounded-[3px] border border-zinc-200 px-2.5 py-1.5"
                  >
                    <a
                      href={`https://www.rcsb.org/structure/${row.entry_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs font-semibold text-zinc-900 hover:underline"
                    >
                      {row.entry_id}
                    </a>
                    <span className="min-w-0 truncate text-2xs text-zinc-500">
                      {[
                        row.exptl_method
                          ?.replace("X-RAY DIFFRACTION", "X-ray")
                          .replace("ELECTRON MICROSCOPY", "cryo-EM")
                          .replace("SOLUTION NMR", "NMR"),
                        row.resolution ? `${row.resolution} Å` : null,
                        row.release_date?.slice(0, 4),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionShell>

          <SectionShell
            title="Drugs & mechanisms — ChEMBL"
            count={drugsQuery.data?.length}
            command={proteinCommands.drugs(accession)}
            loading={drugsQuery.isPending}
            error={drugsQuery.error}
          >
            {(drugsQuery.data ?? []).length === 0 ? (
              <p className="py-1 text-xs text-zinc-500">
                No drugs with a known mechanism target this protein.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {(drugsQuery.data ?? []).map((row) => (
                  <li
                    key={`${row.drug_name}-${row.action_type}`}
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[3px] border border-zinc-200 px-3 py-2"
                  >
                    <FlaskConical className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                    <span className="text-xs font-semibold text-zinc-900">
                      {row.drug_name}
                    </span>
                    {phaseBadge(row)}
                    {row.action_type && (
                      <span className="text-3xs uppercase tracking-wide text-zinc-400">
                        {row.action_type}
                      </span>
                    )}
                    {row.mechanism_of_action && (
                      <span className="w-full text-2xs text-zinc-500">
                        {row.mechanism_of_action}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SectionShell>

          <div className="px-5 py-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onSearchPapers(gene ?? accession)}
            >
              <Search className="h-3.5 w-3.5" aria-hidden />
              Search papers mentioning {gene}
            </Button>
            <p className="mt-2 text-2xs text-zinc-400">
              UniProt, PDB, and ChEMBL are joined live on the accession — the
              same <code className="font-mono">sql -s proteins</code> views the
              CLI exposes.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
};
